import { type PageContainerProps } from '@ant-design/pro-layout';
import { type PButtonProps } from '@porsche-design-system/components-react';
import {
    type DropdownProps as AntdDropdownProps,
    type ButtonProps as AntdButtonProps,
    type SelectProps as AntdSelectProps,
    type RadioProps,
    type RadioGroupProps,
    type InputProps,
    type InputRef,
    type PopconfirmProps as AntdPopconfirmProps,
    type PaginationProps,
    type TooltipProps,
    type FormItemProps as AntdFormItemProps,
} from 'antd';
import { type PickerProps } from 'antd/es/date-picker/generatePicker';
import type { CheckboxGroupProps, CheckboxProps } from 'antd/lib/checkbox';
import type { Gutter } from 'antd/lib/grid/row';
import type { SelectValue } from 'antd/lib/select';
import type { StepsProps as AntdStepsProps, StepProps } from 'antd/lib/steps';
import type { LinkProps } from 'antd/lib/typography/Link';
import type { TextProps } from 'antd/lib/typography/Text';
import {
    type ReactNode,
    createContext,
    type PropsWithChildren,
    type ComponentType,
    type RefAttributes,
    type HTMLAttributes,
} from 'react';
import { type GiftVoucherCodeDataFragment } from '../api/fragments';
import { type PromoCodeDataFragment } from '../api/fragments/PromoCodeData';
import { type CompanyTheme } from '../api/types';
import { type DatePickerValue } from '../calculator/fieldComponents';
import { type DualFieldLayoutProps } from '../calculator/fieldComponents/withDualFieldUI';
import { type SummaryFieldLayoutProps } from '../calculator/fieldComponents/withMonthlyInstalmentFieldUI';
import { type DayjsRangePickerProps } from '../components/DayjsRangePicker';
import { type CheckboxFieldProps } from '../components/fields/CheckboxField';
import { type PhoneFieldProps } from '../components/fields/ci/PhoneField';
import { type CountryFieldProps } from '../components/fields/CountryField';
import { type DatePickerFieldProps } from '../components/fields/DatePickerField';
import { type DisplayFieldProps } from '../components/fields/DisplayField';
import { type DisplayFieldWithUTCOffsetProps } from '../components/fields/DisplayFieldWithUTCOffset';
import { type FixedRangePickerFieldProps } from '../components/fields/FixedRangePickerField';
import { type GenderFieldProps } from '../components/fields/GenderField';
import { type IdentityNumberFieldProps } from '../components/fields/IdentityNumberField';
import { type InputFieldProps } from '../components/fields/InputField';
import { type InputFieldProps as InputNumberFieldProps } from '../components/fields/InputNumberField';
import { type MaritalStatusFieldProps } from '../components/fields/MaritalStatusField';
import { type DraggerFieldProps } from '../components/fields/MultipleDraggerField';
import { type NationalityFieldProps } from '../components/fields/NationalityField';
import { type RaceFieldProps } from '../components/fields/RaceField';
import { type RangePickerFieldProps } from '../components/fields/RangePickerField';
import { type RegionFieldProps } from '../components/fields/RegionField';
import { type ResidentialStatusFieldProps } from '../components/fields/ResidentialStatusField';
import { type SwitchProps } from '../components/fields/SwitchField';
import { type TimePickerFieldProps } from '../components/fields/TimePickerField';
import { type TranslatedInputFieldProps } from '../components/fields/TranslatedInputField';
import { type CompanyPaginatedTableProps } from '../components/PaginatedTableWithContext';
import { type TimePickerFieldProps as TimePickerProps } from '../components/TimePickerField';
import { type CardWithHeaderProps } from './default/CardWithHeader';
import { type ConsentModalProps } from './default/ConsentModal';
import { type ErrorMessageBoxProps } from './default/ErrorMessageBox';
import { type InfoMessageBoxProps } from './default/InfoMessageBox';
import { type ModalProps } from './default/Modal';
import { type DefaultLayoutProps } from './default/Standard/Layout';
import { type TabsProps } from './default/Tabs';
import { type WarningMessageBoxProps } from './default/WarningMessageBox';
import { type ThemeNotificationApi } from './makeNotificationApi';
import { type CarouselProps } from './porsche/Carousel';
import { type SelectFieldProps } from './porsche/Fields/SelectField';

export type ButtonProps = Omit<AntdButtonProps, 'type'> & {
    type?: AntdButtonProps['type'] | 'secondary' | 'tertiary';
    // For link in porsche v2, set this value to render icon in Porsche V2 button
    showIcon?: boolean;
    isCompact?: boolean;
    // note: Porsche V3 button only accepts string for their icon prop,
    //  set this value to render icon in Porsche V3 button
    porscheFallbackIcon?: PButtonProps['icon'];
    porscheFallbackIconSource?: PButtonProps['iconSource'];
};

export type Brightness = { brightness?: 'light' | 'dark' };
// to determine if `SegmentedControl` should be used, applies to Porsche v3
// see: https://appvantage.atlassian.net/browse/AN-2165
export type Segment = { segmented?: boolean };
export type SelectProps = AntdSelectProps<SelectValue> & Brightness & Segment;

export type DropdownProps = AntdDropdownProps & {
    renderDeselectAll?: boolean;
};

export type StepsItemProps = StepProps & {
    index: number;
};

export type StepsProps = Omit<AntdStepsProps, 'items'> & {
    items: StepsItemProps[];
    disableAtFinalStep?: boolean;
    stepRedirectOnClick?: (stepIndex: number) => void;
};

export type FormFields = {
    RangePickerField: ComponentType<RangePickerFieldProps>;
    FixedRangePickerField: ComponentType<FixedRangePickerFieldProps>;
    SelectField: ComponentType<SelectFieldProps>;
    DatePickerField: ComponentType<DatePickerFieldProps>;
    InputField: ComponentType<InputFieldProps>;
    InputNumberField: ComponentType<InputNumberFieldProps>;
    IdentityNumberField: ComponentType<IdentityNumberFieldProps>;
    PhoneAndPrefixField: ComponentType<PhoneFieldProps>;
    NationalityField: ComponentType<NationalityFieldProps>;
    RegionField: ComponentType<RegionFieldProps>;
    CountryField: ComponentType<CountryFieldProps>;
    RaceField: ComponentType<RaceFieldProps>;
    GenderField: ComponentType<GenderFieldProps>;
    ResidentialStatusField: ComponentType<ResidentialStatusFieldProps>;
    MaritalStatusField: ComponentType<MaritalStatusFieldProps>;
    MultipleDraggerField: ComponentType<DraggerFieldProps>;
    TimePickerField: ComponentType<TimePickerFieldProps>;
    TranslatedInputField: ComponentType<TranslatedInputFieldProps>;
    SwitchField: ComponentType<SwitchProps>;
    CheckboxField: ComponentType<CheckboxFieldProps>;
    DisplayField: ComponentType<DisplayFieldProps>;
    PasswordField: ComponentType<InputFieldProps>;
    DisplayFieldWithUTCOffset: ComponentType<DisplayFieldWithUTCOffsetProps>;
};

export type PromoCodeFieldProps = {
    onChange: InputProps['onChange'];
    removePromoCode: () => void;
    value?: string;
    onApply: () => void;
    promoCode: PromoCodeDataFragment;
    promoCodeError: string;
};

export type PromoCodeValidationProps = {
    removePromoCode: () => void;
    promoCode: PromoCodeDataFragment;
    promoCodeError: string;
    promoCodeAmount: string;
};

export type DiscountCodeValidationProps = {
    removePromoCode: () => void;
    removeGiftVoucher: () => void;
    promoCode: PromoCodeDataFragment;
    giftVoucher: GiftVoucherCodeDataFragment;
    discountCodeError: string;
    giftVoucherAmount: string;
    promoCodeAmount: string;
};

export type DisplaySelectFieldProps = {
    value: string;
};
export type VehicleImageProps = {
    src: string;
    fieldKey: string;
    isRemovable: boolean;
    remove: () => void;
};

export type ListItemProps = {
    translationKey: string;
};

export type AddVehicleButtonProps = {
    onClick: () => void;
};

export type CalculatorComponents = {
    fieldSize: number;
    SelectField: ComponentType<SelectProps>;
    SingleField: ComponentType<InputProps>;
    DisplayField: ComponentType<TextProps | { children: ReactNode }>;
    ClickableDisplayField: ComponentType<TextProps | { children: ReactNode }>;
    DatePickerField: ComponentType<PickerProps<DatePickerValue>>;
    DualFieldLayout: ComponentType<DualFieldLayoutProps>;
    SummaryFieldLayout: ComponentType<SummaryFieldLayoutProps>;
    PromoCodeField: ComponentType<PromoCodeFieldProps>;
    PromoCodeValidation: ComponentType<PromoCodeValidationProps>;
    DiscountCodeValidation: ComponentType<DiscountCodeValidationProps>;
    DisplaySelectField: ComponentType<DisplaySelectFieldProps>;
    AddVehicleButton: ComponentType<AddVehicleButtonProps>;
    VehicleImage: ComponentType<VehicleImageProps>;
    FormItem: ComponentType<AntdFormItemProps>;
    FormItemWrapper: ComponentType<{ children: ReactNode }>;
};

export type ConfiguratorFooterContainerProps = {
    visible: boolean;
};

export type ConfiguratorDepositDisplayProps = {
    label: string;
    value: string;
    isSummaryContent: boolean;
};

export type AnchorProps = LinkProps &
    RefAttributes<HTMLElement> & {
        variant?: 'solid' | 'ghost';
        colorSet?: 'primary' | 'secondary' | 'tertiary';
        icon?: ReactNode;
        porscheFallbackIcon?: PButtonProps['icon'];
        porscheFallbackIconSource?: PButtonProps['iconSource'];
    };

// Flexible gutter values for different themes
export type GutterTheme = {
    testDriveModal?: [Gutter, Gutter];
};

export type VehicleCarouselProps = {
    sources: string[];
    fallbackSource: string;
};

export type ThemeComponents = {
    theme: CompanyTheme;
    gutter: GutterTheme;
    Button: ComponentType<ButtonProps>;
    ComparisonButton: ComponentType<ButtonProps>;
    Input: ComponentType<InputProps & React.RefAttributes<InputRef>>;
    PageWithHeader: ComponentType<
        Omit<PropsWithChildren<PageContainerProps>, 'breadcrumb' | 'breadcrumbRender' | 'backIcon'>
    >;
    ConfiguratorDropdown: ComponentType<AntdDropdownProps>;
    Dropdown: ComponentType<DropdownProps>;
    Select: ComponentType<SelectProps>;
    StandardLayout: ComponentType<PropsWithChildren<DefaultLayoutProps>>;
    ConfiguratorLayout: ComponentType<PropsWithChildren<DefaultLayoutProps>>;
    ConfiguratorSubHeader: ComponentType<HTMLAttributes<HTMLDivElement> & { ref?: unknown }>;
    ConfiguratorMessageButton: ComponentType<ButtonProps>;
    ConfiguratorChatIcon?: ComponentType;
    ConfiguratorDepositDisplay: ComponentType<ConfiguratorDepositDisplayProps>;
    MobilityWebpageLayout: ComponentType<PropsWithChildren<DefaultLayoutProps>>;
    BackButton: ComponentType<ButtonProps>;
    CloseButton: ComponentType<ButtonProps>;
    FinderLayout: ComponentType<PropsWithChildren<DefaultLayoutProps>>;
    Title: ComponentType<HTMLAttributes<HTMLDivElement> & { ref?: unknown }>;
    Checkbox: ComponentType<CheckboxProps>;
    CheckboxGroup: ComponentType<CheckboxGroupProps>;
    RangePicker: ComponentType<Omit<DayjsRangePickerProps, 'locale'>>;
    TimePicker: ComponentType<TimePickerProps>;
    FormFields: FormFields;
    LoadingSpinner: ComponentType;
    Modal: ComponentType<ModalProps>;
    ConsentModal: ComponentType<ConsentModalProps>;
    notification: ThemeNotificationApi;
    Radio: ComponentType<RadioProps>;
    RadioGroup: ComponentType<RadioGroupProps>;
    GlobalStyles?: ComponentType;
    CardWithHeader: ComponentType<CardWithHeaderProps>;
    Steps: ComponentType<StepsProps>;
    Search: ComponentType<InputProps>;
    ErrorMessageBox: ComponentType<ErrorMessageBoxProps>;
    InfoMessageBox: ComponentType<InfoMessageBoxProps>;
    WarningMessageBox: ComponentType<WarningMessageBoxProps>;
    JourneySteps: ComponentType<AntdStepsProps>;
    PaginatedTableWithContext: ComponentType<CompanyPaginatedTableProps>;
    Popconfirm: ComponentType<AntdPopconfirmProps>;
    Carousel: ComponentType<CarouselProps>;
    Pagination?: ComponentType<PaginationProps>;
    Tooltip: ComponentType<TooltipProps>;
    Calculator: CalculatorComponents;
    ListItem: ComponentType<ListItemProps>;
    Tabs: ComponentType<TabsProps>;
    Anchor: ComponentType<AnchorProps>;
    LegalText: ComponentType<HTMLAttributes<HTMLSpanElement> & { isBefore?: boolean; $disabled?: boolean }>;
    ArrowDownIcon: JSX.Element;
    VehicleCarousel: ComponentType<VehicleCarouselProps>;
};

const ThemeComponentsContext = createContext<ThemeComponents | null>(null);

export default ThemeComponentsContext;
